// ============================================================================
// MILES — rendu Markdown minimal, sûr et prerender-safe (pas de HTML brut).
// Supporte : ## / ### titres (ids ancrés), paragraphes, listes - et 1., tableaux
// | a | b |, citations >, ---, **gras**, *italique*, [texte](url).
// ============================================================================
import { Link } from "react-router-dom";

export const slugifyHeading = (s) => String(s).normalize("NFKD").replace(/[\u0300-\u036f]/g, "").toLowerCase()
  .replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "").slice(0, 80);

function inline(text, key) {
  const parts = [];
  const re = /(\*\*[^*]+\*\*|\*[^*]+\*|\[[^\]]+\]\([^)\s]+\))/g;
  let last = 0, m, i = 0;
  while ((m = re.exec(text))) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    const tok = m[0];
    if (tok.startsWith("**")) parts.push(<strong key={`${key}-${i++}`} className="font-semibold text-ink">{tok.slice(2, -2)}</strong>);
    else if (tok.startsWith("*")) parts.push(<em key={`${key}-${i++}`}>{tok.slice(1, -1)}</em>);
    else {
      const [, label, href] = tok.match(/\[([^\]]+)\]\(([^)\s]+)\)/);
      const internal = href.startsWith("/");
      parts.push(internal
        ? <Link key={`${key}-${i++}`} to={href} className="font-medium text-miles underline decoration-miles/30 underline-offset-2 hover:decoration-miles">{label}</Link>
        : <a key={`${key}-${i++}`} href={href} target="_blank" rel="noopener noreferrer" className="font-medium text-miles underline decoration-miles/30 underline-offset-2 hover:decoration-miles">{label}</a>);
    }
    last = m.index + tok.length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

export function extractHeadings(md) {
  return (md || "").split("\n").filter((l) => /^## /.test(l)).map((l) => {
    const text = l.replace(/^## /, "").trim();
    return { text, id: slugifyHeading(text) };
  });
}

export function renderMarkdown(md) {
  const lines = (md || "").replace(/\r/g, "").split("\n");
  const out = [];
  let i = 0, k = 0;
  const key = () => `md-${k++}`;
  while (i < lines.length) {
    const line = lines[i];
    if (!line.trim()) { i++; continue; }
    if (/^---+$/.test(line.trim())) { out.push(<hr key={key()} className="my-10 border-ink/[0.08]" />); i++; continue; }
    if (/^#{1,2} /.test(line)) {
      const text = line.replace(/^#{1,2} /, "").trim();
      out.push(<h2 key={key()} id={slugifyHeading(text)} className="mt-12 scroll-mt-28 text-xl font-bold tracking-tight text-ink sm:text-2xl">{inline(text, key())}</h2>);
      i++; continue;
    }
    if (/^### /.test(line)) {
      const text = line.replace(/^### /, "").trim();
      out.push(<h3 key={key()} id={slugifyHeading(text)} className="mt-8 scroll-mt-28 text-base font-bold text-ink sm:text-lg">{inline(text, key())}</h3>);
      i++; continue;
    }
    if (/^\|/.test(line)) {
      const rows = [];
      while (i < lines.length && /^\|/.test(lines[i])) { rows.push(lines[i]); i++; }
      const cells = (r) => r.replace(/^\||\|$/g, "").split("|").map((c) => c.trim());
      const head = cells(rows[0]);
      const body = rows.slice(1).filter((r) => !/^\|\s*:?-{2,}/.test(r)).map(cells);
      out.push(
        <div key={key()} className="my-6 overflow-x-auto rounded-2xl border border-ink/10">
          <table className="w-full min-w-[520px] text-left text-sm">
            <thead className="bg-miles-ice/70 text-[11px] uppercase tracking-[0.14em] text-miles-deep">
              <tr>{head.map((h, j) => <th key={j} className="px-4 py-3 font-semibold">{inline(h, key())}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-ink/[0.06]">
              {body.map((r, ri) => (
                <tr key={ri} className="bg-white">
                  {r.map((c, j) => <td key={j} className={`px-4 py-3 align-top ${j === 0 ? "font-medium text-ink" : "text-slate"}`}>{inline(c, key())}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>);
      continue;
    }
    if (/^> /.test(line)) {
      const buf = [];
      while (i < lines.length && /^> ?/.test(lines[i]) && lines[i].trim()) { buf.push(lines[i].replace(/^> ?/, "")); i++; }
      out.push(
        <aside key={key()} className="my-6 rounded-2xl border border-miles/15 bg-miles-ice/50 px-5 py-4 text-sm leading-relaxed text-miles-deep">
          {inline(buf.join(" "), key())}
        </aside>);
      continue;
    }
    if (/^[-*] /.test(line)) {
      const items = [];
      while (i < lines.length && /^[-*] /.test(lines[i])) { items.push(lines[i].replace(/^[-*] /, "")); i++; }
      out.push(<ul key={key()} className="my-5 space-y-2 pl-1">{items.map((it, j) => (
        <li key={j} className="flex gap-3 text-[15px] leading-relaxed text-slate"><span className="mt-[9px] h-1.5 w-1.5 shrink-0 rounded-full bg-miles" />{<span>{inline(it, key())}</span>}</li>))}</ul>);
      continue;
    }
    if (/^\d+\. /.test(line)) {
      const items = [];
      while (i < lines.length && /^\d+\. /.test(lines[i])) { items.push(lines[i].replace(/^\d+\. /, "")); i++; }
      out.push(<ol key={key()} className="my-5 space-y-3">{items.map((it, j) => (
        <li key={j} className="flex gap-3 text-[15px] leading-relaxed text-slate">
          <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-miles-deep font-mono text-[11px] font-semibold text-white">{j + 1}</span>
          <span className="pt-0.5">{inline(it, key())}</span></li>))}</ol>);
      continue;
    }
    const buf = [];
    while (i < lines.length && lines[i].trim() && !/^(#{1,3} |[-*] |\d+\. |> |\||---)/.test(lines[i])) { buf.push(lines[i].trim()); i++; }
    out.push(<p key={key()} className="my-5 text-[15px] leading-[1.75] text-slate sm:text-base">{inline(buf.join(" "), key())}</p>);
  }
  return out;
}
